import React, { Component } from "react";
import { Button, Card, Spin, Select, Input, Icon, Tooltip } from "antd";
import { Table } from "components/table";
import { CAMS_VIEW_COLUMNS_NEW } from "../../constants/columns";
import PropTypes from "prop-types";
import { STATE_STATUS } from "../../constants";
import { List, OrderedMap } from "immutable";
import { CaseService } from "../../services";
import CreditSummaryCams from "./credit-summary-cams";
import "../../styles/cam-view.css";

const STATUS_CONFIG = {
    GREEN: { label: "GREEN", border: "#1fb26a", bg: "rgba(31,178,106,0.08)", text: "#167a3a", bar: "#10b981" },
    RED: { label: "RED", border: "#ff4d4f", bg: "rgba(255,77,79,0.06)", text: "#c43b3b", bar: "#ff4d4f" },
    AMBER: { label: "AMBER", border: "#f59e0b", bg: "rgba(245,158,11,0.06)", text: "#b56f00", bar: "#f59e0b" },
};

const CategoryCard = ({ title, passed, total, statusKey = "AMBER", iconType, onClick, active }) => {
    const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.AMBER;
    const percent = total === 0 ? 0 : Math.round((passed / total) * 100);
    return (
        <div
            className={`cv-ov-card ${onClick ? "clickable" : ""} ${active ? "active-overview" : ""}`}
            style={{ borderColor: cfg.border }}
            onClick={onClick}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={e => { if (e.key === "Enter" && onClick) onClick(); }}
        >
            <div className="cv-ov-inner" style={{ backgroundColor: cfg.bg }}>
                <div className="cv-ov-left">
                    <div className={`cv-ov-icon icon-${statusKey.toLowerCase()}`}><Icon type={iconType || "appstore"} /></div>
                </div>

                <div className="cv-ov-right">
                    <div className="cv-ov-meta">
                        <div className="cv-ov-cat">{title}</div>
                        <div className="cv-ov-sub small">
                            <span className="cv-ov-status" style={{ color: cfg.text }}>{cfg.label}</span>
                            <span className="cv-ov-pass"> {passed}/{total} Pass</span>
                        </div>
                    </div>

                    <div className="cv-ov-progress">
                        <div className="cv-ov-track">
                            <div className="cv-ov-fill" style={{ width: `${percent}%`, backgroundColor: cfg.bar }} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const onCellGenerator = (handleSave, _) => record => ({
    record,
    editable: true,
    dataIndex: _.dataIndex,
    title: record.header || _.title,
    handleSave,
    rules: record.rules || _.rules,
    fieldType: record.fieldType || _.fieldType,
    filesMultiple: _.filesMultiple ? _.filesMultiple : false,
    options: record.options || _.options,
    viewable: true,
    max: record.max
});


const CustomSearchBar = ({ value, onChange, placeholder }) => {
    return (
        <div className="cam-search-wrapper">
            <Icon type="search" className="cam-search-icon" />
            <Input
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className="cam-search-input"
            />
        </div>
    );
};

const STATUS_UI = {
    GREEN: {
        bg: "#f0fdf4",
        border: "#16a34a",
        pillBg: "#dcfce7",
        pillColor: "#16a34a"
    },
    BLUE: {
        bg: "#eff6ff",
        border: "#3b82f6",
        pillBg: "#dbeafe",
        pillColor: "#2563eb"
    },
    LIGHT_GREEN: {
        bg: "#f2f6e9",
        border: "#a1ecb0",
        pillBg: "#ecfccb",
        pillColor: "#4d7c0f"
    },
    RED: {
        bg: "#fef2f2",
        border: "#ef4444",
        pillBg: "#fee2e2",
        pillColor: "#dc2626"
    },
    AMBER: {
        bg: "#fff7ed",
        border: "#f59e0b",
        pillBg: "#ffedd5",
        pillColor: "#d97706"
    }
};

const STATUS_PILL = {
    APPROVED: {
        bg: "#dcfce7",
        color: "#16a34a",
        icon: "check-circle",
        label: "Approved"
    },
    ACTION: {
        bg: "#fee2e2",
        color: "#dc2626",
        icon: "exclamation-circle",
        label: "Requires Action"
    }
};


const SECTION_ICON_MAP = {
    "Financials": "bank",
    "Applicant Profile": "user",
    "Anchor Relationship": "apartment",
    "GSTIN Analysis": "file-text",
    "Banking": "credit-card",
    "Credit History": "idcard",
    "Financial Performance": "line-chart",
    "Portfolio Performance": "pie-chart",
    "Bureau Assessment": "profile",
};



class CAMViewNew extends Component {
    updateReplyButtonRef = React.createRef();
    state = {
        camEngineTableData: [],
        newData: new OrderedMap({}),
        canDataLoader: false,
        submittedComments: {},
        editInfo: null,
        selectedFactNameMap: {},
        commentTextMap: {},
        replyTexts: {},
        replyEditInfo: null,
        creditSummaryData: null,
        camViewCommentsData: [],
        openSections: {},
        searchQuery: "",
        activeOverviewFilter: null,
        filteredGroupedData: null,
        expandedComments: {},
        filterType: "ALL"
    };

    componentDidMount() {
        CaseService.getCamViewCreditSummary(this.props.caseId).then(res => {
            this.setState({ creditSummaryData: res?.data?.result?.choices[0].message?.content });
        }).catch(err => console.error('Error fetching summary:', err));

        if (this.props.camEngineStatus === STATE_STATUS.FETCHED) this.creditEnhancementDbViewFn();
        else this.props.fetchCamEngineView(this.props.caseId);

        if (this.props.camEngineCommentsStatus === STATE_STATUS.FETCHED) this.camViewCommentsFn();
        else this.props.fetchCamEngineViewComments(this.props.caseId);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.camEngineStatus == STATE_STATUS.FETCHED && prevProps.camEngineStatus == STATE_STATUS.FETCHING) {
            this.creditEnhancementDbViewFn();
        }
        if (this.props.camEngineCommentsStatus == STATE_STATUS.FETCHED && prevProps.camEngineCommentsStatus == STATE_STATUS.FETCHING) {
            this.camViewCommentsFn();
        }

        // when groupedCamData (source) or filters change, recompute filteredGroupedData
        if (
            prevState.searchQuery !== this.state.searchQuery ||
            // prevState.activeOverviewFilter !== this.state.activeOverviewFilter ||
            prevState.filterType !== this.state.filterType ||
            prevState.groupedCamData !== this.state.groupedCamData
        ) {
            this.applyFilters();
        }

    }

    groupByTableTitle = (data) => {
        const categoryTitleMap = {
            General: "Applicant Profile",
            GST: "GSTIN Analysis",
            Bureau: "Bureau Assessment",
            MPA: "Anchor Relationship",
            Anchor: "Anchor Relationship",
            Ledger: "Ledger",
            Banking: "Banking",
            Financials: "Financials",
            DB: "Portfolio Performance",
            Geography: "Portfolio Performance",
        };
        const grouped = {};
        (data || []).forEach(item => {
            const title = categoryTitleMap[item.category] || "Others";
            if (!grouped[title]) grouped[title] = [];
            grouped[title].push(item);
        });
        return grouped;
    };

    groupCommentsByTableTitle = (commentsData = []) => {
        const categoryTitleMap = {
            General: "Applicant Profile",
            GST: "GSTIN Analysis",
            Bureau: "Bureau Assessment",
            MPA: "Anchor Relationship",
            Anchor: "Anchor Relationship",
            Ledger: "Ledger",
            Banking: "Banking",
            Financials: "Financials",
            DB: "Portfolio Performance",
            Geography: "Portfolio Performance",
        };
        const grouped = {};
        commentsData.forEach(section => {
            const title = categoryTitleMap[section.title] || section.title;
            if (!grouped[title]) grouped[title] = [];
            section.comments.forEach(comment => {
                grouped[title].push({
                    ...comment,
                    factName: comment.fact_name,
                    comment: comment.comment,
                    replies: comment.replies || [],
                    created_by: comment.created_by_name || [],
                });
            });
        });
        return grouped;
    };

    creditEnhancementDbViewFn = () => {
        const camsApiData = this.props.camEngineData || {};
        for (let key in camsApiData) if (camsApiData.hasOwnProperty(key)) camsApiData[key].key2 = key;

        const sortedData = Object.entries(camsApiData || {}).sort(([a], [b]) => {
            const la = a.split('_').pop(); const lb = b.split('_').pop();
            const ia = !isNaN(la); const ib = !isNaN(lb);
            if (ia && ib) return parseInt(la) - parseInt(lb);
            if (ia) return 1; if (ib) return -1; return 0;
        });
        const sortedObject = Object.fromEntries(sortedData || []);
        const CamViewDataApi = Object.values(sortedObject || {});
        const camsViewData = [];

        (CamViewDataApi || []).forEach((el, idx) => {
            if (el?.category) {
                const factValue = el?.fact_value === true
                    ? "True"
                    : (el?.fact_value === false || el?.fact_value === "" || el?.fact_value === null)
                        ? "False"
                        : el?.fact_value;
                camsViewData.push({
                    s_no: idx + 1,
                    category: el?.category,
                    fact_name: el?.fact_name,
                    fact_value: typeof factValue === 'string' && factValue.length > 30
                        ? (<Tooltip title={factValue}><span>{factValue.slice(0, 30) + '...'}</span></Tooltip>)
                        : factValue,
                    policy: typeof el?.policy == "boolean" ? String(el?.policy) : el?.policy || "NA",
                    output: el?.output == true ? "Pass" : el?.output == false ? "Fail" : "Not Applicable",
                });
            }
        });

        const grouped = this.groupByTableTitle(camsViewData);

        const firstOpen = {};
        const titles = Object.keys(grouped);
        if (titles.length) firstOpen[titles[0]] = true;

        this.setState({
            groupedCamData: grouped,
            newData: new OrderedMap(camsApiData),
            // openSections: firstOpen
        }, () => {
            this.applyFilters();
        });
    };

    camViewCommentsFn = () => {
        const commentsData = this.props.camEngineCommentsData?.comments_replies_data || [];
        this.setState({ submittedComments: this.groupCommentsByTableTitle(commentsData) });
    };

    toggleSection = (title) => {
        this.setState(prev => ({ openSections: { ...prev.openSections, [title]: !prev.openSections[title] } }));
    };

    // getCustomTag = (_, key) => {
    //     if (key == "deviation") {
    //         return <label className="br-4" style={{ backgroundColor: _ === "Yes" ? "#f4f494" : "" }}> {_} </label>
    //     }
    //     return <div style={{ width: "100%" }}><label className="txt-light br-4"
    //         style={{ backgroundColor: _ === "Pass" ? "rgb(134 220 134)" : _ === "Fail" ? "rgb(238 142 142)" : "rgb(226 226 30)", padding: "0 10px", display: "inline-block", width: "70%" }}>{_}</label></div>
    // }

    getCustomTag = (value, key) => {
        if (key !== "output") return value;
    
        const isPass = value === "Pass";
        const isFail = value === "Fail";
    
        return (
            <div className="cv-output-cell">
                <span className={`cv-output-pill ${isPass ? "pass" : "fail"}`}>
                    {value}
                </span>
                <span className={`cv-output-pill ${isPass ? "approved" : "action"}`}>
                    <Icon type={isPass ? "check-circle" : "exclamation-circle"} />
                    {isPass ? "Approved" : "Requires Action"}
                </span>
            </div>
        );
    };
    


    approveCase = () => {
        this.setState({ canDataLoader: true });
        CaseService.approveCamsCase(this.props.caseId)
            .then(() => this.setState({ canDataLoader: false }))
            .catch(err => { this.setState({ canDataLoader: false }); console.log(err); });
    }

    handleSaveMultipleEntries = (record, values) => {
        if ((record.key || record.key == 0) && this.state.newData.size) {
            let data = this.state.newData.map((_, k) => {
                if (_.key2 == record.key2) return { ..._, ...values, key: record.key };
                return _;
            });
            this.setState({ newData: data });
        }
    }

    toggleCommentsSection = (title) => {
        this.setState(prev => ({
            expandedComments: {
                ...prev.expandedComments,
                [title]: !prev.expandedComments[title],
            },
        }));
    };


    handleEditComment(title, index) {
        const commentData = this.state.submittedComments[title][index];

        this.setState(prev => ({
            selectedFactNameMap: {
                ...prev.selectedFactNameMap,
                [title]: Array.isArray(commentData.fact_name)
                    ? commentData.fact_name
                    : [commentData.fact_name]
            },
            commentTextMap: {
                ...prev.commentTextMap,
                [title]: commentData.comment || ""
            },
            editInfo: { title, index }
        }));
    }



    handleCommentSubmit = (title) => {
        const { selectedFactNameMap, commentTextMap, submittedComments, editInfo } = this.state;
        const selected = selectedFactNameMap[title] || [];
        const selectedArray = Array.isArray(selected) ? selected : [selected];
        const commentText = (commentTextMap && commentTextMap[title]) || '';
        if (!selectedArray.length || !commentText.trim()) return;
        const isEdit = !!editInfo && editInfo.title === title;
        const payload = { title, comment: commentText.trim(), fact_name: selectedArray.length === 1 ? selectedArray[0] : selectedArray };
        if (isEdit) {
            const existing = submittedComments[title] && submittedComments[title][editInfo.index];
            if (existing && existing.id) payload.comment_id = existing.id;
        }
        this.submitCamViewComments(payload, isEdit ? 'patch' : 'post');
        this.setState(prev => ({ selectedFactNameMap: prev.selectedFactNameMap, commentTextMap: { ...prev.commentTextMap, [title]: '' }, editInfo: null }));
    };

    handleReplyInputChange = (e, title, commentIndex) => {
        const key = `${commentIndex}_${title}`;
        this.setState({ replyTexts: { ...this.state.replyTexts, [key]: e.target.value } });
    };

    handleReplySubmit = (title, commentIndex) => {
        const key = `${commentIndex}_${title}`; const replyText = this.state.replyTexts[key];
        const comment = this.state.submittedComments[title][commentIndex]; if (!replyText) return;
        const isEdit = this.state.replyEditInfo?.replyIndex != null;
        const replyPayload = { title, comment_id: comment.id, reply: replyText, ...(isEdit && { reply_id: comment.replies[this.state.replyEditInfo.replyIndex].id }) };
        this.submitCamViewComments(replyPayload, isEdit ? 'patch' : 'post');
        this.setState({ replyTexts: { ...this.state.replyTexts, [key]: '' }, replyEditInfo: null, activeReply: null });
    };

    submitCamViewComments = (payload, method = 'post') => {
        const { caseId } = this.props;
        const apiCall = method === 'patch' ? CaseService.updateCamViewComments(caseId, payload) : CaseService.saveCamViewComments(caseId, payload);
        apiCall.then(() => this.props.fetchCamEngineViewComments(this.props.caseId)).catch(err => console.error("Error saving/updating comments:", err));
    };

    handleEditReply = (title, commentIndex, replyIndex, currentReply) => {
        const key = `${commentIndex}_${title}`;
        this.setState({ replyTexts: { ...this.state.replyTexts, [key]: currentReply }, activeReply: { title, commentIndex }, replyEditInfo: { title, commentIndex, replyIndex } }, () => {
            if (this.updateReplyButtonRef.current) this.updateReplyButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    };

    applyFilters = () => {
        const {
            groupedCamData,
            searchQuery,
            activeOverviewFilter,
            filterType
        } = this.state;
        if (!groupedCamData) {
            this.setState({ filteredGroupedData: null });
            return;
        }
        const q = (searchQuery || "").trim().toLowerCase();
        const result = {};

        Object.entries(groupedCamData).forEach(([title, rows]) => {
            // if (activeOverviewFilter && activeOverviewFilter !== title) return;

            const passedCount = rows.filter(r => r.output === "Pass").length;
            const failedCount = rows.filter(r => r.output === "Fail").length;

            if (filterType === "PASS" && passedCount === 0) return;
            if (filterType === "FAIL" && failedCount === 0) return;

            let filteredRows = rows;

            if (q) {
                filteredRows = rows.filter(r => {
                    const factName = (r.fact_name || "").toString().toLowerCase();
                    const policy = (r.policy || "").toString().toLowerCase();
                    const value = (r.fact_value || r.value || "").toString().toLowerCase();

                    return (
                        factName.includes(q) ||
                        policy.includes(q) ||
                        value.includes(q)
                    );
                });
            }
            if (!filteredRows.length) return;
            if (filterType === "PASS") {
                filteredRows = filteredRows.filter(r => r.output === "Pass");
            }
            if (filterType === "FAIL") {
                filteredRows = filteredRows.filter(r => r.output === "Fail");
            }
            if (filteredRows.length) {
                result[title] = filteredRows;
            }
        });

        this.setState({ filteredGroupedData: result });
    };


    handleSearchChange = (e) => {
        const newQ = e?.target?.value ?? "";
        this.setState({ searchQuery: newQ });
    };

    handleSearchClear = () => {
        this.setState({ searchQuery: "" });
    };

    handleOverviewCardClick = (title) => {
        this.setState(prev => ({
            openSections: {
                [title]: true
            }
        }), () => {
            const el = document.getElementById(`section-${title}`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        });
    };


    getSectionUI = (passed, failed, total) => {
        if (failed === 0 && passed > 0) {
            return {
                key: "GREEN",
                bg: "#f0fdf4",
                border: "#16a34a",
                pill: "approved"
            };
        }

        if (failed > passed) {
            return {
                key: "RED",
                bg: "#fff1f2",
                border: "#ef4444",
                pill: "action"
            };
        }

        return {
            key: "AMBER",
            bg: "#fff7ed",
            border: "#f59e0b",
            pill: "action"
        };
    };

    getSectionStatus = (passed, failed, total) => {
        if (passed === total && total > 0) return "GREEN";
        if (passed > failed) return "BLUE";
        if (passed > 0 && passed < failed) return "LIGHT_GREEN";
        if (failed > 0 && passed === 0) return "RED";
        return "AMBER";
    };



    renderSectionHeader = (title, rows) => {
        const total = rows.length;
        const passed = rows.filter(r => r.output === "Pass").length;
        const failed = rows.filter(r => r.output === "Fail").length;

        const statusKey = this.getSectionStatus(passed, failed, total);
        const ui = STATUS_UI[statusKey];
        const isOpen = !!this.state.openSections[title];
        const isApproved = failed === 0 && total > 0;
        const pill = isApproved ? STATUS_PILL.APPROVED : STATUS_PILL.ACTION;

        return (
            <button
                type="button"
                className="cv-section-header"
                style={{
                    background: ui.bg,
                    borderLeftColor: ui.border
                }}
                onClick={() => this.toggleSection(title)}
                aria-expanded={isOpen}
            >
                <div className="cv-section-left">
                    <div className="cv-section-icon">
                        <div className={`cv-section-icon icon-${statusKey.toLowerCase()}`}>
                            <Icon type={SECTION_ICON_MAP[title] || "appstore"} />
                        </div>

                    </div>

                    <div className="cv-section-meta">
                        <div className="cv-section-title">{title}</div>
                        {/* <div className="cv-section-sub">
                            Primary summary / short description
                        </div> */}
                    </div>
                </div>

                <div className="cv-section-right">
                    <div
                        className="cv-pill"
                        style={{
                            background: pill.bg,
                            color: pill.color
                        }}
                    >
                        <Icon type={pill.icon} />
                        {pill.label}
                    </div>

                    <div className="cv-counts">
                        <span className="cv-pass">
                            <Icon type="check-circle" />
                            {passed} Pass
                        </span>
                        <span className="cv-fail">
                            <Icon type="close-circle" />
                            {failed} Fail
                        </span>
                    </div>

                    <div className={`cv-chevron ${isOpen ? "open" : ""}`}>
                        <Icon type="down" />
                    </div>
                </div>
            </button>
        );
    };



    getSummaryFromGroupedData = () => {
        const { groupedCamData } = this.state;

        let totalParameters = 0;
        let passCount = 0;
        let failCount = 0;
        Object.values(groupedCamData || {}).forEach(section => {
            (section || []).forEach(param => {
                totalParameters += 1;

                if (param.output === "Pass") passCount += 1;
                if (param.output === "Fail") failCount += 1;
            });
        });

        const passPercentage =
            totalParameters > 0
                ? Math.round((passCount / totalParameters) * 100)
                : 0;

        let riskLabel = "Low Risk";
        if (passPercentage < 75 && passPercentage >= 50) riskLabel = "Medium Risk";
        if (passPercentage < 50) riskLabel = "High Risk";

        return {
            totalParameters,
            passCount,
            failCount,
            passPercentage,
            riskLabel
        };
    };


    renderSummaryCards = () => {
        const {
            totalParameters,
            passCount,
            failCount,
            passPercentage,
            riskLabel
        } = this.getSummaryFromGroupedData();

        return (
            <div className="cv-summary-grid">

                <div className="cv-summary-card info">
                    <div>
                        <div className="cv-summary-title">Total Parameters</div>
                        <div className="cv-summary-value">{totalParameters}</div>
                        <div className="cv-summary-subtext">
                            Across all categories
                        </div>
                    </div>
                    <div className="cv-summary-icon">
                        <Icon type="bar-chart" />
                    </div>
                </div>

                <div className="cv-summary-card success">
                    <div>
                        <div className="cv-summary-title">Passed Checks</div>
                        <div className="cv-summary-value">{passCount}</div>
                        <div className="cv-summary-subtext">
                            Successfully met criteria
                        </div>
                    </div>
                    <div className="cv-summary-icon">
                        <Icon type="check-circle" />
                    </div>
                </div>

                <div className="cv-summary-card danger">
                    <div>
                        <div className="cv-summary-title">Failed Checks</div>
                        <div className="cv-summary-value">{failCount}</div>
                        <div className="cv-summary-subtext">
                            Require attention
                        </div>
                    </div>
                    <div className="cv-summary-icon">
                        <Icon type="check-circle" />
                    </div>
                </div>

                <div className="cv-summary-card warning">
                    <div className="cv-summary-title">Risk Level</div>
                    <div className="cv-summary-value">{riskLabel}</div>

                    <div className="cv-risk-bar-div">
                        <div className="cv-risk-bar">
                            <div
                                className="cv-risk-progress"
                                style={{ width: `${passPercentage}%` }}
                            />
                        </div>
                        <div className="cv-risk-percent">{passPercentage}%</div>
                    </div>

                    <div className="cv-summary-subtext">
                        Based on overall pass rate across all categories
                    </div>

                    <div className="cv-summary-icon">
                        ⚠️
                    </div>
                </div>


            </div>
        );
    };


    render() {
        const groupedSource = this.state.filteredGroupedData ?? this.state.groupedCamData ?? {};
        const hasAnyResults = groupedSource && Object.keys(groupedSource).length > 0 &&
            Object.values(groupedSource).some(rows => rows && rows.length);
        return (
            <>
                <Spin spinning={this.props.camEngineStatus === STATE_STATUS.FETCHING || this.state.canDataLoader}>
                    <Card headStyle={{ display: 'none' }} extra={[]}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ margin: 0 }}>Credit Insight Dashboard</h2>
                            <Button type="primary" onClick={() => this.approveCase()} key="approve" style={{ backgroundColor: "#46b046", border: "none" }}>
                                <Icon type="check-circle" theme="twoTone" twoToneColor="#52c41a" /> Approve
                            </Button>
                        </div>
                    </Card>
                </Spin>
                <br />

                <div className="cv-summary-wrapper cv-section-wrap">
                    {this.renderSummaryCards()}
                </div>
                {/* {this.state.creditSummaryData && <CreditSummaryCams creditSummaryData={this.state.creditSummaryData} />} */}
                <br />
                <div className="cv-search-container">
                    <div className="cv-search-row">
                        <CustomSearchBar
                            value={this.state.searchQuery}
                            onChange={this.handleSearchChange}
                            placeholder="Search parameters across all categories..."
                        />
                        <div className="cv-filter-group">
                            <span className="cv-filter-label">Filter:</span>
                            <Button
                                type={this.state.filterType === "ALL" ? "primary" : "default"}
                                onClick={() => this.setState({ filterType: "ALL" })}
                            >
                                All
                            </Button>
                            <Button
                                type={this.state.filterType === "PASS" ? "primary" : "default"}
                                onClick={() => this.setState({ filterType: "PASS" })}
                            >
                                Pass
                            </Button>
                            <Button
                                type={this.state.filterType === "FAIL" ? "primary" : "default"}
                                onClick={() => this.setState({ filterType: "FAIL" })}
                            >
                                Fail
                            </Button>
                        </div>
                        {this.state.activeOverviewFilter && (
                            <Button onClick={() => this.setState({ activeOverviewFilter: null })}>
                                Show all categories
                            </Button>
                        )}
                    </div>
                </div>

                <div className="cv-category-sticky">
                    <h3 className="cv-category-heading">Category Overview</h3>
                    <div className="cv-overview-grid">
                        {this.state.groupedCamData &&
                            Object.entries(this.state.groupedCamData).map(([title, rows]) => {
                                const total = rows.length;
                                const passed = rows.filter(r => r.output === "Pass").length;
                                const failed = rows.filter(r => r.output === "Fail").length;

                                let statusKey = "AMBER";
                                if (passed === total && total > 0) statusKey = "GREEN";
                                else if (failed > 0 && failed >= passed) statusKey = "RED";

                                return (
                                    <CategoryCard
                                        key={title}
                                        title={title}
                                        passed={passed}
                                        total={total}
                                        statusKey={statusKey}
                                        iconType={SECTION_ICON_MAP[title]}
                                        onClick={() => this.handleOverviewCardClick(title)}
                                        active={this.state.activeOverviewFilter === title}
                                    />
                                );
                            })}
                    </div>
                </div>

                {hasAnyResults ? (
                    Object.entries(groupedSource || {}).map(([title, rows]) => {
                        const isOpen = !!this.state.openSections[title];
                        if (!rows || !rows.length) return null;

                        const total = rows.length;
                        const passed = rows.filter(r => r.output === "Pass").length;
                        const failed = rows.filter(r => r.output === "Fail").length;
                        let statusKey = "AMBER";
                        if (passed === total && total > 0) statusKey = "GREEN";
                        else if (failed > 0 && failed >= passed) statusKey = "RED";
                        else statusKey = "AMBER";
                        return (
                            <div key={title} id={`section-${title}`} className="cv-section-wrap">
                                <Card className={"cv-section-card"} bodyStyle={{ padding: 0 }}>
                                    {this.renderSectionHeader(title, rows)}

                                    <div className={`cv-section-body ${isOpen ? "open" : "collapsed"}`} aria-hidden={!isOpen}>
                                        <div className="cv-table-card">
                                            <Table
                                                className="ant-col padless-card custom-align-table-body-cam"
                                                columns={CAMS_VIEW_COLUMNS_NEW(this.getCustomTag).map(({ editable, ...rest }) => {
                                                    if (!onCellGenerator || !editable) return rest;
                                                    return { onCell: onCellGenerator(this.handleSaveMultipleEntries, rest), ...rest };
                                                })}
                                                data={List(rows).toList()}
                                                size="middle"
                                                pagination={false}
                                                showFilters={false}
                                                noRowSelection={true}
                                                showColumnSelector={false}
                                                scrollY={500}
                                            />
                                        </div>

                                        <div className="cv-comments-wrapper">
                                            <div
                                                className="cv-comments-header"
                                                onClick={() => this.toggleCommentsSection(title)}
                                            >
                                                <div className="cv-comments-header-left">
                                                    <Icon type="message" />
                                                    <span className="cv-comments-title">
                                                        {title} – Comments & Notes
                                                    </span>
                                                    <span className="cv-comments-count">
                                                        {(this.state.submittedComments?.[title]?.length || 0)}
                                                    </span>
                                                </div>

                                                <Icon
                                                    type="down"
                                                    className={`cv-comments-chevron ${this.state.expandedComments?.[title] ? "open" : ""
                                                        }`}
                                                />
                                            </div>

                                            {this.state.expandedComments?.[title] && (
                                                <div className="cv-comments-body">

                                                    <div className="cv-add-comment">
                                                        <Select
                                                            placeholder="Select a Parameter"
                                                            style={{ width: "100%", marginBottom: 10 }}
                                                            mode="multiple"
                                                            value={this.state.selectedFactNameMap?.[title] || []}
                                                            onChange={(value) =>
                                                                this.setState(prev => ({
                                                                    selectedFactNameMap: {
                                                                        ...prev.selectedFactNameMap,
                                                                        [title]: value,
                                                                    },
                                                                }))
                                                            }
                                                        >
                                                            {Array.from(new Set(rows.map(r => r.fact_name)))
                                                                .filter(Boolean)
                                                                .map(fact => (
                                                                    <Select.Option key={fact} value={fact}>
                                                                        {fact}
                                                                    </Select.Option>
                                                                ))}
                                                        </Select>

                                                        <Input.TextArea
                                                            placeholder="Add your comment or note..."
                                                            value={this.state.commentTextMap?.[title] || ""}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                this.setState(prev => ({
                                                                    commentTextMap: {
                                                                        ...prev.commentTextMap,
                                                                        [title]: val,
                                                                    },
                                                                }));
                                                            }}
                                                            autoSize={{ minRows: 2 }}
                                                        />

                                                        <Button
                                                            type="primary"
                                                            style={{ marginTop: 8 }}
                                                            disabled={
                                                                !(this.state.selectedFactNameMap?.[title]?.length) ||
                                                                !(this.state.commentTextMap?.[title]?.trim())
                                                            }
                                                            onClick={() => this.handleCommentSubmit(title)}
                                                        >
                                                            {this.state.editInfo?.title === title ? "Update" : "Save"}
                                                        </Button>
                                                    </div>

                                                    <div className="cv-comments-list">
                                                        {(this.state.submittedComments?.[title] || []).map((entry, idx) => (
                                                            <div key={idx} className="cv-comment-card">

                                                                <div className="cv-comment-main">
                                                                    <div className="cv-comment-fact-div">
                                                                        <div className="cv-comment-fact">
                                                                            {Array.isArray(entry.fact_name)
                                                                                ? entry.fact_name.join(" | ")
                                                                                : entry.fact_name}
                                                                        </div>

                                                                        <div className="cv-comment-text">
                                                                            {entry.comment}
                                                                        </div>

                                                                        <div className="cv-comment-meta">
                                                                            — {entry.created_by} {entry.created_at && `• ${entry.created_at}`}
                                                                        </div>
                                                                    </div>
                                                                    <div className="cv-comment-actions">
                                                                        {entry.user_id === this.props.user && (
                                                                            <Icon
                                                                                type="edit"
                                                                                onClick={() => this.handleEditComment(title, idx)}
                                                                            />
                                                                        )}
                                                                        <span
                                                                            className="cv-reply-link"
                                                                            onClick={() =>
                                                                                this.setState({ activeReply: { title, commentIndex: idx } })
                                                                            }
                                                                        >
                                                                            Reply
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {(entry.replies || []).map((r, rIdx) => (
                                                                    <div key={rIdx} className="cv-reply">
                                                                        ↳ {r.reply}
                                                                        <div className="cv-reply-meta">
                                                                            — {r.created_by_name}
                                                                        </div>
                                                                    </div>
                                                                ))}

                                                                {this.state.activeReply?.title === title &&
                                                                    this.state.activeReply?.commentIndex === idx && (
                                                                        <div className="cv-reply-input">
                                                                            <Input.TextArea
                                                                                rows={2}
                                                                                value={
                                                                                    this.state.replyTexts?.[`${idx}_${title}`] || ""
                                                                                }
                                                                                onChange={(e) =>
                                                                                    this.handleReplyInputChange(e, title, idx)
                                                                                }
                                                                            />
                                                                            <Button
                                                                                size="small"
                                                                                type="primary"
                                                                                onClick={() => this.handleReplySubmit(title, idx)}
                                                                            >
                                                                                Reply
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                </Card>
                            </div>
                        );
                    }))
                    : (
                        <div className="cv-empty-state">
                            <Icon type="exclamation-circle" className="cv-empty-icon" />
                            <h3>No Parameters Found</h3>
                            <p>
                                No parameters match your current search and filter criteria.
                                Try adjusting your filters or clearing the search.
                            </p>
                            <Button
                                type="primary"
                                onClick={() =>
                                    this.setState({
                                        searchQuery: "",
                                        filterType: "ALL",
                                        activeOverviewFilter: null,
                                        filteredGroupedData: null
                                    })
                                }
                            >
                                Clear All Filters
                            </Button>
                        </div>
                    )
                }
            </>
        );
    }
}

CAMViewNew.propTypes = {
    caseId: PropTypes.number,
    camEngineData: PropTypes.object,
    fetchCamEngineView: PropTypes.func,
    fetchCaseDetail: PropTypes.func,
    camEngineStatus: PropTypes.string,
    camEngineCommentsStatus: PropTypes.string,
    camEngineCommentsData: PropTypes.object,
    user: PropTypes.any
};

export default CAMViewNew;
